import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import StaffShell from "../components/StaffShell";
import { api } from "../lib/api";

const STATUSES = ["checked_in", "diagnosing", "awaiting_approval", "awaiting_parts", "in_progress", "ready_for_pickup", "closed", "cancelled"];

export default function Intake() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", make: "", model: "", year: "",
    vin: "", mileage: "", service: "Electrical diagnosis", concern: "", initial_status: "checked_in",
    assignment: "", priority: "normal", arrival_confirmed: false, contact_permission: false });
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const req = params.get("request");
    if (!req) return;
    api("/staff/requests").then((d) => {
      const r = d.requests.find((x) => x.request_number === req);
      if (r) setF((s) => ({ ...s, ...r.data, request_id: req, year: String(r.data.year) }));
    }).catch(() => {});
  }, [params]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api("/staff/checkin", {
        method: "POST",
        body: { ...f, year: parseInt(f.year, 10), mileage: f.mileage ? parseInt(f.mileage, 10) : null,
          idempotency_key: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())) },
      });
      setDone(res);
      window.setTimeout(() => nav(`/tickets/${res.id}`), 1600);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const F = (k, label, props = {}) => (
    <div className="field" key={k}>
      <label>{label}</label>
      {props.options ? (
        <select value={f[k]} onChange={set(k)}>{props.options.map((o) => <option key={o}>{o}</option>)}</select>
      ) : (
        <input value={f[k]} onChange={set(k)} type={props.type || "text"} required={props.required} placeholder={props.placeholder} data-testid={`intake-${k}-input`} />
      )}
    </div>
  );

  return (
    <StaffShell title="Store vehicle check-in">
      {done ? (
        <div className="ok-box" data-testid="checkin-success">
          <CheckCircle2 size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
          <strong>{done.number} created.</strong> Receipt email queued. Opening the ticket…
        </div>
      ) : (
        <div className="card" style={{ padding: 28, maxWidth: 860 }}>
          <p className="form-note" style={{ marginBottom: 20 }}>
            Check-in requires two confirmations: the vehicle physically arrived, and the customer gave permission
            to be contacted. Creating a ticket issues an immutable CCC- number and queues the customer receipt.
          </p>
          {f.request_id && <p className="pill confirmed" style={{ marginBottom: 16 }}>Linked request: {f.request_id}</p>}
          <form onSubmit={submit} data-testid="checkin-form">
            <div className="field-pair">
              {F("first_name", "First name", { required: true })}
              {F("last_name", "Last name", { required: true })}
            </div>
            <div className="field-pair">
              {F("phone", "Phone", { required: true, type: "tel" })}
              {F("email", "Email", { required: true, type: "email" })}
            </div>
            <div className="field-pair">
              {F("make", "Make", { required: true })}
              {F("model", "Model", { required: true })}
            </div>
            <div className="field-pair">
              {F("year", "Year", { required: true, type: "number" })}
              {F("mileage", "Mileage (optional)", { type: "number" })}
            </div>
            {F("vin", "VIN (optional, exact normalized history)", { placeholder: "17 characters" })}
            {F("service", "Service requested", { options: ["Electrical diagnosis", "Battery & charging concern", "Lights & wiring concern", "Starter & alternator concern", "Callback / not sure"] })}
            <div className="field"><label>Reported concern</label><textarea required rows={3} value={f.concern} onChange={set("concern")} data-testid="intake-concern-input" /></div>
            <div className="field-pair">
              {F("initial_status", "Initial status", { options: STATUSES })}
              {F("priority", "Priority", { options: ["normal", "high", "low"] })}
            </div>
            {F("assignment", "Assign to (optional)", { placeholder: "Miguel" })}
            <label className="consent">
              <input type="checkbox" required checked={f.arrival_confirmed} onChange={set("arrival_confirmed")} data-testid="intake-arrival-checkbox" />
              <span><strong>Physical arrival confirmed</strong> — the vehicle is at the shop now.</span>
            </label>
            <label className="consent">
              <input type="checkbox" required checked={f.contact_permission} onChange={set("contact_permission")} data-testid="intake-permission-checkbox" />
              <span><strong>Contact permission confirmed</strong> — the customer agreed to be contacted about this visit.</span>
            </label>
            {error && <div className="error-box" role="alert" data-testid="checkin-error">{error}</div>}
            <button className="btn btn-red" disabled={busy} data-testid="checkin-submit">{busy ? "Creating…" : "Create Ticket"}</button>
          </form>
        </div>
      )}
    </StaffShell>
  );
}
